import React from "react";
import {
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableRow,
  FormControl,
  FormHelperText,
  InputLabel,
} from "@mui/material";
import { MeetingHostSchedule } from "../../../../model/meeting_host_schedule";
import { useHostOptions } from "./useHostOptions";

interface HostSelectionProps {
  value: MeetingHostSchedule; // Ensure value is defined
  /** Partner scope of the series: its staff are offered as hosts too. */
  partnerId?: string;
  onChange: (value: MeetingHostSchedule) => void;
}

const days = [
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
  "sunday",
];

function capitalizeFirstLetter(str: string) {
  return str.charAt(0).toUpperCase() + str.slice(1);
}

export const RecurringHostSelection: React.FC<HostSelectionProps> = ({
  value,
  partnerId,
  onChange,
}) => {
  const { options, byId, partnerError } = useHostOptions(
    partnerId,
    ["default", ...days].map((key) => value[key]),
  );
  const handleScheduleChange = (
    day: keyof MeetingHostSchedule,
    hostId: string,
  ) => {
    const newValue: MeetingHostSchedule = {
      ...value,
      [day]: byId[hostId],
    };
    onChange(newValue);
  };

  return (
    <FormControl fullWidth margin="normal" error={!!partnerError}>
      {partnerError && <FormHelperText>{partnerError}</FormHelperText>}
      <FormControl fullWidth margin="normal">
        <InputLabel id="default-host-label">Select a default host:</InputLabel>
        <Select
          labelId="default-host-label"
          value={value.default?.id ?? ""} // Ensure default value is defined
          onChange={(e) =>
            handleScheduleChange("default", e.target.value as string)
          }
        >
          <MenuItem value="">Select a default host</MenuItem>
          {options.map(({ host, label }) => (
            <MenuItem key={host.id} value={host.id}>
              {label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <br />
      <br />
      <div>
        <p>Select hosts for each day:</p>
        <Table>
          <TableBody>
            {days.map((day) => {
              return (
                <TableRow key={day} data-testid={`${day}-row-test`}>
                  <TableCell>{capitalizeFirstLetter(day)}</TableCell>
                  <TableCell>
                    <FormControl fullWidth disabled={!value.default}>
                      <InputLabel id={`${day}-label`}>Select a host</InputLabel>
                      <Select
                        labelId={`${day}-label`}
                        value={
                          value[day as keyof MeetingHostSchedule]?.id || ""
                        }
                        onChange={(e) =>
                          handleScheduleChange(
                            day as keyof MeetingHostSchedule,
                            e.target.value as string,
                          )
                        }
                      >
                        <MenuItem value="" disabled={!value.default}>
                          Select a host
                        </MenuItem>
                        {options.map(({ host, label }) => (
                          <MenuItem key={host.id} value={host.id}>
                            {label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
    </FormControl>
  );
};
